// D&D rules helpers shared across the portal
export const ABILITIES = [
  { key: "strength", label: "STR" },
  { key: "dexterity", label: "DEX" },
  { key: "constitution", label: "CON" },
  { key: "intelligence", label: "INT" },
  { key: "wisdom", label: "WIS" },
  { key: "charisma", label: "CHA" },
];

export const SKILLS = {
  Acrobatics: "dexterity",
  "Animal Handling": "wisdom",
  Arcana: "intelligence",
  Athletics: "strength",
  Deception: "charisma",
  History: "intelligence",
  Insight: "wisdom",
  Intimidation: "charisma",
  Investigation: "intelligence",
  Medicine: "wisdom",
  Nature: "intelligence",
  Perception: "wisdom",
  Performance: "charisma",
  Persuasion: "charisma",
  Religion: "intelligence",
  "Sleight of Hand": "dexterity",
  Stealth: "dexterity",
  Survival: "wisdom",
};

export const mod = (score) => Math.floor((Number(score || 10) - 10) / 2);
export const fmtMod = (m) => (m >= 0 ? `+${m}` : `${m}`);

export const RARITY_COLORS = {
  Common: "#9ca3af",
  Uncommon: "#34d399",
  Rare: "#60a5fa",
  "Very Rare": "#c084fc",
  Legendary: "#f0b429",
  Artifact: "#fb7185",
};

export const SCHOOL_COLORS = {
  Abjuration: "#60a5fa",
  Conjuration: "#f59e0b",
  Divination: "#a78bfa",
  Enchantment: "#f472b6",
  Evocation: "#fb7185",
  Illusion: "#22d3ee",
  Necromancy: "#84cc16",
  Transmutation: "#2dd4bf",
};

export function rollDie(sides) {
  return Math.floor(Math.random() * sides) + 1;
}

// Deterministic "fixed HP" progression (5e average method) so leveling up AND
// down always resolve to the same max HP for a given level.
//   Level 1: hitDie + conMod
//   Each level after: ceil((hitDie+1)/2) + conMod  (minimum 1 per level)
export function hpPerLevelAfterFirst(hitDie, conMod) {
  return Math.max(1, Math.ceil((Number(hitDie) + 1) / 2) + Number(conMod || 0));
}

export function maxHpForLevel(hitDie, level, conMod = 0) {
  const die = Number(hitDie) || 8;
  const lvl = Math.max(1, Math.min(20, Number(level) || 1));
  const first = Math.max(1, die + Number(conMod || 0));
  return first + (lvl - 1) * hpPerLevelAfterFirst(die, conMod);
}

export const POINT_BUY_COST = { 8: 0, 9: 1, 10: 2, 11: 3, 12: 4, 13: 5, 14: 7, 15: 9 };
export const POINT_BUY_BUDGET = 27;
export const STANDARD_ARRAY = [15, 14, 13, 12, 10, 8];

export function profBonusForLevel(level) {
  return Math.ceil(Number(level || 1) / 4) + 1;
}

export function pointBuyTotal(scores) {
  return Object.values(scores).reduce((sum, v) => sum + (POINT_BUY_COST[v] ?? 99), 0);
}

// ---------- Spellcasting (SRD approximations, D&D Beyond style) ----------
export const CASTER_ABILITY = {
  Bard: "charisma", Cleric: "wisdom", Druid: "wisdom", Sorcerer: "charisma",
  Warlock: "charisma", Wizard: "intelligence", Paladin: "charisma", Ranger: "wisdom",
};

export const isCaster = (cls) => cls in CASTER_ABILITY;

// "prepared" casters choose from their whole list each day (count = mod + level)
export const PREPARES = { Cleric: true, Druid: true, Wizard: true, Paladin: true };

const CANTRIPS_BY_LEVEL = {
  Bard: [2, 2, 2, 3], Cleric: [3, 3, 3, 4], Druid: [2, 2, 2, 3],
  Sorcerer: [4, 4, 4, 5], Warlock: [2, 2, 2, 3], Wizard: [3, 3, 3, 4],
};

export function cantripsKnown(cls, level) {
  const t = CANTRIPS_BY_LEVEL[cls];
  if (!t) return 0;
  return t[Math.min(Math.max(1, level), t.length) - 1];
}

// Number of leveled spells the character knows / can prepare
export function spellsKnown(cls, level, abilityMod = 0) {
  const lvl = Math.max(1, level || 1);
  switch (cls) {
    case "Wizard": return 6 + (lvl - 1) * 2; // spellbook
    case "Cleric":
    case "Druid": return Math.max(1, abilityMod + lvl);
    case "Paladin": return lvl < 2 ? 0 : Math.max(1, abilityMod + Math.floor(lvl / 2));
    case "Bard": { const t = [4, 5, 6, 7, 8, 9, 10, 11, 12, 14, 15, 15, 16, 18, 19, 19, 20, 22, 22, 22]; return t[Math.min(lvl, t.length) - 1]; }
    case "Sorcerer": { const t = [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 12, 13, 13, 14, 14, 15, 15, 15, 15]; return t[Math.min(lvl, t.length) - 1]; }
    case "Warlock": { const t = [2, 3, 4, 5, 6, 7, 8, 9, 10, 10, 11, 11, 12, 12, 13, 13, 14, 14, 15, 15]; return t[Math.min(lvl, t.length) - 1]; }
    case "Ranger": return lvl < 2 ? 0 : Math.floor(lvl / 2) + 1;
    default: return 0;
  }
}

// Highest spell level slot available (cap for what can be chosen)
export function maxSpellLevel(cls, level) {
  const lvl = Math.max(1, level || 1);
  if (cls === "Warlock") return Math.min(5, Math.ceil(lvl / 2));
  if (cls === "Paladin" || cls === "Ranger") return Math.min(5, Math.ceil(Math.max(0, lvl - 1) / 4) + (lvl >= 2 ? 1 : 0));
  // full casters: spell level ~ ceil(level/2) capped at 9
  return Math.min(9, Math.ceil(lvl / 2));
}

// ---------- Spell slots (cube trackers) ----------
const FULL_CASTER_SLOTS = {
  1: [2], 2: [3], 3: [4, 2], 4: [4, 3], 5: [4, 3, 2], 6: [4, 3, 3], 7: [4, 3, 3, 1],
  8: [4, 3, 3, 2], 9: [4, 3, 3, 3, 1], 10: [4, 3, 3, 3, 2], 11: [4, 3, 3, 3, 2, 1],
  12: [4, 3, 3, 3, 2, 1], 13: [4, 3, 3, 3, 2, 1, 1], 14: [4, 3, 3, 3, 2, 1, 1],
  15: [4, 3, 3, 3, 2, 1, 1, 1], 16: [4, 3, 3, 3, 2, 1, 1, 1], 17: [4, 3, 3, 3, 2, 1, 1, 1, 1],
  18: [4, 3, 3, 3, 3, 1, 1, 1, 1], 19: [4, 3, 3, 3, 3, 2, 1, 1, 1], 20: [4, 3, 3, 3, 3, 2, 2, 1, 1],
};
const HALF_CASTER_SLOTS = {
  1: [], 2: [2], 3: [3], 4: [3], 5: [4, 2], 6: [4, 2], 7: [4, 3], 8: [4, 3], 9: [4, 3, 2],
  10: [4, 3, 2], 11: [4, 3, 3], 12: [4, 3, 3], 13: [4, 3, 3, 1], 14: [4, 3, 3, 1],
  15: [4, 3, 3, 2], 16: [4, 3, 3, 2], 17: [4, 3, 3, 3, 1], 18: [4, 3, 3, 3, 1],
  19: [4, 3, 3, 3, 2], 20: [4, 3, 3, 3, 2],
};

// Returns { "1": count, "2": count, ... } of spell slots for the class+level.
export function spellSlots(cls, level) {
  const lvl = Math.max(1, Math.min(20, level || 1));
  if (cls === "Warlock") {
    const count = lvl >= 17 ? 4 : lvl >= 11 ? 3 : lvl >= 2 ? 2 : 1;
    const slvl = Math.min(5, Math.ceil(lvl / 2));
    return { [slvl]: count };
  }
  let arr = [];
  if (["Bard", "Cleric", "Druid", "Sorcerer", "Wizard"].includes(cls)) arr = FULL_CASTER_SLOTS[lvl] || [];
  else if (["Paladin", "Ranger"].includes(cls)) arr = HALF_CASTER_SLOTS[lvl] || [];
  else return {};
  const out = {};
  arr.forEach((n, i) => { if (n > 0) out[i + 1] = n; });
  return out;
}

// Apply a race's ability bonuses on top of base scores.
export function applyRaceBonuses(base, race) {
  const out = { ...base };
  if (race?.ability_bonuses) {
    for (const [k, v] of Object.entries(race.ability_bonuses)) {
      if (ABILITIES.find((a) => a.key === k)) out[k] = (out[k] || 0) + v;
    }
  }
  return out;
}

